<template>
  <div class="support-page">
    <div class="support-hero">
      <div class="support-hero__inner">
        <h1 class="support-hero__title">{{ $t('How can we help?') }}</h1>
        <p class="support-hero__subtitle">{{ $t('Choose a topic below to get started.') }}</p>
      </div>
    </div>

    <div class="support-content">
      <!-- Step 1: Reason selection -->
      <div v-if="!selectedReason" class="support-content__grid">
        <SupportReasonSelector @select="onReasonSelected" />
      </div>

      <!-- Step 2: Selected reason flow -->
      <div v-else class="support-content__form">
        <button class="support-back" @click="goBack">
          <span>&larr;</span> {{ $t('Back') }}
        </button>

        <h2 class="support-content__heading">{{ $t(selectedReason.label) }}</h2>

        <!-- Booking lookup for "Manage or cancel" -->
        <SupportBookingLookupForm v-if="selectedReason.key === 'manage_booking'" />

        <!-- Contact form for partner inquiry -->
        <SupportContactForm
          v-else-if="selectedReason.key === 'partner_inquiry'"
          :reason-subject="$t(selectedReason.label)"
          recipient-type="partner"
        />

        <!-- Contact form for everything else -->
        <SupportContactForm v-else :reason-subject="$t(selectedReason.label)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { $siteConfig } = useNuxtApp()
const brand = $siteConfig?.value?.brand || 'Tripening'

useHead({
  title: `Support | ${brand}`,
  meta: [
    { name: 'robots', content: 'noindex, nofollow' },
    { name: 'description', content: `Get help with your ${brand} booking. Manage reservations, request cancellations, or contact our support team.` },
  ],
})

const selectedReason = ref<{ key: string; label: string; description: string; icon: string } | null>(null)

function onReasonSelected(reason: { key: string; label: string; description: string; icon: string }) {
  selectedReason.value = reason
}

function goBack() {
  selectedReason.value = null
}
</script>

<style lang="scss" scoped>
.support-page {
  min-height: 60vh;
}

.support-hero {
  background: var(--light-grey);
  width: 100%;
  padding: 48px var(--page-horizontal-padding) 40px;

  &__inner {
    max-width: var(--content-max-width);
    margin: 0 auto;
    text-align: center;
  }

  &__title {
    font-size: 34px;
    font-weight: 600;
    color: var(--black);
    margin: 0 0 8px;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 16px;
    font-weight: 400;
    color: var(--dark-grey);
    margin: 0;
  }
}

.support-content {
  padding: 40px var(--page-horizontal-padding) 60px;

  &__grid {
    max-width: var(--content-max-width);
    margin: 0 auto;
  }

  &__form {
    max-width: 560px;
    margin: 0 auto;
  }

  &__heading {
    font-size: 22px;
    font-weight: 600;
    color: var(--black);
    margin: 0 0 20px;
  }
}

.support-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--blue);
  font-size: 14px;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 24px;

  &:hover {
    text-decoration: underline;
  }
}
</style>
